import React, { useState, useEffect } from 'react';
import { Phone, Lock, Compass, Building2, UserCheck, AlertCircle, ListOrdered, Sparkles, Info, BookOpen, Upload, Image, Zap, CheckCircle2, Cloud, ArrowRight, TrendingDown, FileImage, X, Layers, Barcode, ShoppingCart } from 'lucide-react';
import type { User, EditionType } from '../../../../shared/types/index.js';
import { RichTextEditor } from '../../../../shared/components/RichTextEditor.js';
import { LazyBookCover } from '../../../../shared/components/LazyBookCover.js';
import { compressImageFile, convertToWebP } from '../../../../shared/utils/imageCompressor.js';
import { saveLocalCover, deleteRemoteCover } from '../../../../shared/utils/dexieDb.js';
import { cleanTableOfContents, hasValidTableOfContents } from '../../../../shared/utils/tocHelper.js';
import { CategoryDropdown } from './CategoryDropdown.js';
import { CustomSelect, type CustomSelectOption } from './CustomSelect.js';
import type { BookFormData } from './bookFormConstants.js';

interface BookMetadataFieldsProps {
  formData: BookFormData;
  setFormData: React.Dispatch<React.SetStateAction<BookFormData>>;
  formErrors: Record<string, string>;
  isCustomCategory: boolean;
  setIsCustomCategory: (val: boolean) => void;
  customCategory: string;
  setCustomCategory: (val: string) => void;
  user: User | null;
  publishers: Array<{ id: string; name: string; publisherCode: string }>;
  isApproved?: boolean;
  isTitleLocked?: boolean;
  onOpenFlowGuide?: () => void;
  onOpenProfile?: () => void;
}

export const BookMetadataFields: React.FC<BookMetadataFieldsProps> = ({
  formData,
  setFormData,
  formErrors,
  isCustomCategory,
  setIsCustomCategory,
  customCategory,
  setCustomCategory,
  user,
  publishers,
  isApproved: isApprovedProp,
  isTitleLocked: isTitleLockedProp,
  onOpenFlowGuide,
  onOpenProfile,
}) => {
  const isApproved = isApprovedProp ?? isTitleLockedProp ?? formData.status === 'disetujui';
  const isTitleLocked = isApproved;

  const [isCompressingImage, setIsCompressingImage] = useState<boolean>(false);
  const [isFormContentsLoading, setIsFormContentsLoading] = useState<boolean>(true);

  useEffect(() => {
    // Show a high-fidelity shimmering skeleton loader on first mount
    // to simulate professional secure indexing / fetching of rich content from server.
    const timer = setTimeout(() => {
      setIsFormContentsLoading(false);
    }, 600);
    return () => clearTimeout(timer);
  }, []);

  const [compressionStats, setCompressionStats] = useState<{
    originalSizeKB: number;
    compressedSizeKB: number;
    savingsPercentage: number;
  } | null>(null);

  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const [isDeletingCover, setIsDeletingCover] = useState(false);

  const handleCoverFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadError(null);

    // Validate file type
    const allowedTypes = ['image/webp', 'image/jpeg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      setUploadError('Hanya diperbolehkan mengunggah gambar dengan format WebP, JPEG, atau PNG.');
      return;
    }

    // Validate file size (5MB)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      setUploadError('Ukuran file maksimal adalah 5MB.');
      return;
    }

    setIsCompressingImage(true);
    try {
      const result = await convertToWebP(file, 400, 0.78);
      
      // Save locally to Dexie and attempt Cloudflare R2 sync (passing previous cover url for automatic cleanup)
      const previousUrl = formData.coverUrl;
      const coverId = `cov-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`;
      const savedUrl = await saveLocalCover(coverId, result.dataUrl, file.name, previousUrl);

      setFormData((prev) => ({ ...prev, coverUrl: savedUrl }));
      
      if (savedUrl.startsWith('blob:')) {
        console.info('[UIBN] Cover saved to local storage, background sync in progress...');
      }
      const originalSizeKB = Math.round(file.size / 1024);
      setCompressionStats({
        originalSizeKB,
        compressedSizeKB: result.sizeKB,
        savingsPercentage: originalSizeKB > 0 
          ? Math.max(0, Math.round(((originalSizeKB - result.sizeKB) / originalSizeKB) * 100))
          : 0,
      });
    } catch (err) {
      console.error('Error compressing or saving image:', err);
      setUploadError('Gagal memproses gambar. Silakan coba file lain.');
    } finally {
      setIsCompressingImage(false);
    }
  };

  // Option definitions for CustomSelects
  const publisherOptions: CustomSelectOption[] = publishers.map((p) => ({
    value: p.id,
    label: p.name,
    badge: `Kode: ${p.publisherCode || '0000'}`,
    badgeColor: 'bg-purple-100 text-purple-700',
    icon: <Building2 className="w-4 h-4" />,
  }));

  const editionTypeOptions: CustomSelectOption[] = [
    { value: 'orisinal', label: 'Orisinal (Edisi Pertama)', description: 'Naskah karya perdana yang belum direvisi' },
    { value: 'revisi', label: 'Revisi (Mayor / Minor)', description: 'Perbaikan isi, bab baru, atau koreksi naskah' },
    { value: 'cetakan_baru', label: 'Cetakan Baru / Reprint', description: 'Pencetakan ulang tanpa perubahan isi' },
    { value: 'digital', label: 'Edisi Digital (E-Book / Web)', description: 'Format digital interaktif atau e-pub' },
    { value: 'terjemahan', label: 'Terjemahan Bahasa', description: 'Alih bahasa dari naskah bahasa lain' },
    { value: 'edisi_khusus', label: 'Edisi Khusus / Kolektor', description: 'Edisi hardcover, boxset, atau khusus' },
    { value: 'edisi_saku', label: 'Edisi Saku / Ringkas', description: 'Ukuran saku / format ringkas' },
  ];

  const canChoosePublisherPath = user?.role === 'penulis' || user?.role === 'admin';

  const statusOptions: CustomSelectOption[] = isApproved
    ? [
        {
          value: 'disetujui',
          label: 'Disetujui (UIBN Terbit)',
          badge: 'Resmi Terbit',
          badgeColor: 'bg-emerald-100 text-emerald-800 border border-emerald-300',
        },
      ]
    : [
        { value: 'draft', label: 'Draft (Naskah)', badge: 'Draft', badgeColor: 'bg-slate-100 text-slate-700' },
        ...((!formData.selectedPublisherId || !canChoosePublisherPath)
          ? [{ value: 'diajukan', label: 'Diajukan (Ke Admin Pusat)', badge: 'Diajukan', badgeColor: 'bg-indigo-100 text-indigo-700' }]
          : []),
        ...(canChoosePublisherPath && formData.selectedPublisherId
          ? [{ value: 'menunggu_penerbit', label: 'Kirim ke Penerbit (Kurasi)', badge: 'Penerbit', badgeColor: 'bg-purple-100 text-purple-700' }]
          : []),
        ...(user?.role === 'penerbit'
          ? [{ value: 'menunggu_penerbit', label: 'Menunggu Verifikasi Penerbit', badge: 'Review', badgeColor: 'bg-amber-100 text-amber-700' }]
          : []),
        ...(user?.role === 'admin'
          ? [
              ...(formData.selectedPublisherId
                ? [{ value: 'diajukan', label: 'Diajukan (Ke Admin Pusat)', badge: 'Diajukan', badgeColor: 'bg-indigo-100 text-indigo-700' }]
                : []),
              { value: 'disetujui', label: 'Disetujui (UIBN Terbit)', badge: 'Resmi Terbit', badgeColor: 'bg-emerald-100 text-emerald-700' },
              { value: 'diarsipkan', label: 'Diarsipkan', badge: 'Arsip', badgeColor: 'bg-slate-200 text-slate-800' },
            ]
          : []),
      ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-left">
      {/* Informative Rule Notice Banner */}
      <div className="sm:col-span-2 p-3 rounded-xl bg-slate-100/90 border border-slate-200/90 text-xs text-slate-700 flex items-start gap-2.5">
        <Lock className="w-4 h-4 text-slate-600 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <span className="font-bold text-slate-800">
              Ketentuan Pengajuan UIBN:
            </span>
            {onOpenFlowGuide && (
              <button
                type="button"
                onClick={onOpenFlowGuide}
                className="text-indigo-600 hover:text-indigo-800 font-bold flex items-center gap-1 text-[11px] underline cursor-pointer shrink-0"
              >
                <Compass className="w-3.5 h-3.5" />
                <span>Lihat Alur Lengkap</span>
              </button>
            )}
          </div>
          <p className="text-[11px] text-slate-600 mt-0.5 leading-relaxed">
            Tersedia 2 cara pengajuan: <strong>1. Akun Penulis</strong> (Jalur Mandiri atau Jalur Penerbit Terdaftar) &amp; <strong>2. Akun Penerbit</strong> (Pengajuan langsung ke Admin UIBN). Selama proses pengajuan, <strong>metadata buku dikunci dari pengeditan</strong>. E-Sertifikat digital, widget HTML, barcode 1D, dan QR Code 2D akan otomatis diterbitkan dan siap digunakan setelah disetujui resmi.
          </p>
        </div>
      </div>

      {/* Title */}
      <div className="sm:col-span-2">
        <div className="flex items-center justify-between mb-1">
          <label className="block text-xs font-semibold text-slate-700">
            Judul Buku <span className="text-rose-500">*</span>
          </label>
          {isTitleLocked && (
            <span className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-800 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-md">
              <Lock className="w-3 h-3 text-amber-600" />
              <span>Terkunci Permanen (UIBN Terbit)</span>
            </span>
          )}
        </div>
        <div className="relative">
          <input
            type="text"
            readOnly={isTitleLocked}
            value={formData.title}
            onChange={(e) => !isTitleLocked && setFormData((prev) => ({ ...prev, title: e.target.value }))}
            placeholder="Contoh: Lentera di Ujung Cakrawala"
            className={`w-full px-3.5 py-2 text-sm border rounded-xl transition duration-150 ${
              isTitleLocked
                ? 'bg-slate-100/90 text-slate-800 border-slate-200 cursor-not-allowed select-none pl-9 font-semibold'
                : formErrors.title
                ? 'border-rose-300 focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 bg-rose-50/20'
                : 'border-slate-300 focus:outline-hidden focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-600 bg-white'
            }`}
          />
          {isTitleLocked && (
            <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          )}
        </div>
        {isTitleLocked ? (
          <p className="text-[11px] text-slate-500 mt-1.5 flex items-start gap-1">
            <Info className="w-3.5 h-3.5 text-indigo-500 shrink-0 mt-0.5" />
            <span>
              Judul utama telah terdaftar resmi dalam sertifikat UIBN. Untuk perubahan judul buku secara substansial, silakan ajukan sebagai edisi / revisi baru. Anda tetap dapat memperbarui sinopsis, sampul, link pembelian, daftar isi, catatan edisi, dan metadata lainnya.
            </span>
          </p>
        ) : (
          formErrors.title && (
            <p className="text-xs text-rose-500 mt-1 flex items-center gap-1 font-medium">
              <AlertCircle className="w-3.5 h-3.5 shrink-0" />
              <span>{formErrors.title}</span>
            </p>
          )
        )}
      </div>

      {/* Subtitle */}
      <div>
        <label className="block text-xs font-semibold text-slate-700 mb-1">
          Subjudul Buku (Opsional)
        </label>
        <input
          type="text"
          value={formData.subtitle}
          onChange={(e) => setFormData((prev) => ({ ...prev, subtitle: e.target.value }))}
          placeholder="Contoh: Eksplorasi Filosofis Menembus Ruang & Waktu"
          className="w-full px-3.5 py-2 text-sm border border-slate-300 rounded-xl focus:outline-hidden focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-600 bg-white"
        />
      </div>

      {/* Original Title */}
      <div>
        <label className="block text-xs font-semibold text-slate-700 mb-1">
          Judul Asli (Jika Terjemahan)
        </label>
        <input
          type="text"
          value={formData.originalTitle}
          onChange={(e) => setFormData((prev) => ({ ...prev, originalTitle: e.target.value }))}
          placeholder="Opsional"
          className="w-full px-3.5 py-2 text-sm border border-slate-300 rounded-xl focus:outline-hidden focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-600 bg-white"
        />
      </div>

      {/* Category Dropdown */}
      <CategoryDropdown
        category={formData.category}
        onSelectCategory={(cat) => setFormData((prev) => ({ ...prev, category: cat }))}
        isCustomCategory={isCustomCategory}
        setIsCustomCategory={setIsCustomCategory}
        customCategory={customCategory}
        setCustomCategory={setCustomCategory}
        error={formErrors.category}
      />

      {/* Author */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="block text-xs font-semibold text-slate-700">
            Nama Penulis <span className="text-rose-500">*</span>
          </label>
          {isApproved && (
            <span className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-800 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-md">
              <Lock className="w-3 h-3 text-amber-600" />
              <span>Terkunci (UIBN Terbit)</span>
            </span>
          )}
        </div>
        <div className="relative">
          <input
            type="text"
            readOnly={isApproved}
            value={formData.authorName}
            onChange={(e) => !isApproved && setFormData((prev) => ({ ...prev, authorName: e.target.value }))}
            placeholder="Nama penulis"
            className={`w-full px-3.5 py-2 text-sm border rounded-xl transition duration-150 ${
              isApproved
                ? 'bg-slate-100/90 text-slate-800 border-slate-200 cursor-not-allowed select-none pl-9 font-semibold'
                : formErrors.authorName
                ? 'border-rose-300 focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 bg-rose-50/20 bg-white'
                : 'border-slate-300 focus:outline-hidden focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-600 bg-white'
            }`}
          />
          {isApproved && (
            <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          )}
        </div>
        {isApproved ? (
          <p className="text-[11px] text-slate-500 mt-1">
            Nama pengarang/penulis terikat permanen dengan hak cipta dan nomor sertifikat UIBN.
          </p>
        ) : (
          formErrors.authorName && (
            <p className="text-xs text-rose-500 mt-1 flex items-center gap-1 font-medium">
              <AlertCircle className="w-3.5 h-3.5 shrink-0" />
              <span>{formErrors.authorName}</span>
            </p>
          )
        )}
      </div>

      {/* Publisher */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="block text-xs font-semibold text-slate-700">
            {canChoosePublisherPath ? 'Pilih Jalur Penerbitan' : 'Nama Lembaga Penerbit'} <span className="text-rose-500">*</span>
          </label>
          {isApproved && (
            <span className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-800 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-md">
              <Lock className="w-3 h-3 text-amber-600" />
              <span>Terkunci (UIBN Terbit)</span>
            </span>
          )}
        </div>

        {isApproved ? (
          <div className="space-y-1.5">
            <div className="relative">
              <input
                type="text"
                readOnly
                value={
                  formData.selectedPublisherId
                    ? `${formData.publisherName || formData.selectedPublisherName || 'Penerbit Mitra'} (Penerbit Terdaftar)`
                    : `${formData.publisherName || 'Penerbit Mandiri (Self-Publishing)'}`
                }
                className="w-full pl-9 pr-3.5 py-2 text-sm border border-slate-200 rounded-xl bg-slate-100/90 text-slate-800 font-semibold cursor-not-allowed select-none"
              />
              <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>
            <p className="text-[11px] text-slate-500">
              Lembaga penerbit atau jalur penerbitan telah tercatat resmi dalam kode UIBN dan sertifikat.
            </p>
          </div>
        ) : canChoosePublisherPath ? (
          <div className="space-y-3">
            {/* 2 Path Choice Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <label
                className={`p-3 rounded-xl border cursor-pointer transition flex items-start gap-2.5 ${
                  !formData.selectedPublisherId
                    ? 'border-indigo-600 bg-indigo-50/50 shadow-2xs ring-1 ring-indigo-500/20'
                    : 'border-slate-200 hover:bg-slate-50'
                }`}
              >
                <input
                  type="radio"
                  name="pub_type"
                  checked={!formData.selectedPublisherId}
                  onChange={() =>
                    setFormData((prev) => ({
                      ...prev,
                      selectedPublisherId: undefined,
                      selectedPublisherName: undefined,
                      selectedPublisherCode: undefined,
                      publisherName: 'Penerbit Mandiri',
                      status: 'diajukan',
                    }))
                  }
                  className="w-4 h-4 text-indigo-600 focus:ring-indigo-500 mt-0.5 shrink-0 cursor-pointer"
                />
                <div className="text-xs">
                  <div className="font-bold text-slate-900 flex items-center gap-1">
                    <UserCheck className="w-3.5 h-3.5 text-indigo-600" />
                    <span>Jalur Mandiri</span>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">
                    Langsung masuk ke Admin UIBN untuk peninjauan resmi.
                  </p>
                </div>
              </label>

              <label
                className={`p-3 rounded-xl border cursor-pointer transition flex items-start gap-2.5 ${
                  !!formData.selectedPublisherId
                    ? 'border-purple-600 bg-purple-50/50 shadow-2xs ring-1 ring-purple-500/20'
                    : 'border-slate-200 hover:bg-slate-50'
                }`}
              >
                <input
                  type="radio"
                  name="pub_type"
                  checked={!!formData.selectedPublisherId}
                  onChange={() => {
                    if (publishers && publishers.length > 0) {
                      const first = publishers[0];
                      setFormData((prev) => ({
                        ...prev,
                        selectedPublisherId: first.id,
                        selectedPublisherName: first.name,
                        selectedPublisherCode: first.publisherCode,
                        publisherName: first.name,
                        status: 'menunggu_penerbit',
                      }));
                    } else {
                      setFormData((prev) => ({
                        ...prev,
                        selectedPublisherId: 'pending',
                        publisherName: '',
                        status: 'menunggu_penerbit',
                      }));
                    }
                  }}
                  className="w-4 h-4 text-purple-600 focus:ring-purple-500 mt-0.5 shrink-0 cursor-pointer"
                />
                <div className="text-xs">
                  <div className="font-bold text-slate-900 flex items-center gap-1">
                    <Building2 className="w-3.5 h-3.5 text-purple-600" />
                    <span>Penerbit Terdaftar</span>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">
                    Masuk ke penerbit dulu untuk kurasi, lalu lanjut ke Admin UIBN.
                  </p>
                </div>
              </label>
            </div>

            {formData.selectedPublisherId ? (
              <div className="space-y-3">
                {publishers && publishers.length > 0 ? (
                  <div>
                    <CustomSelect
                      label="Pilih Lembaga Penerbit Mitra UIBN"
                      required
                      value={formData.selectedPublisherId}
                      onChange={(selectedId) => {
                        const pub = publishers.find((p) => p.id === selectedId);
                        if (pub) {
                          setFormData((prev) => ({
                            ...prev,
                            selectedPublisherId: pub.id,
                            selectedPublisherName: pub.name,
                            selectedPublisherCode: pub.publisherCode,
                            publisherName: pub.name,
                          }));
                        }
                      }}
                      options={publisherOptions}
                      placeholder="Pilih Lembaga Penerbit..."
                      error={formErrors.publisherName}
                    />
                  </div>
                ) : (
                  <div className="p-2.5 text-xs text-amber-700 bg-amber-50 rounded-xl border border-amber-200 italic">
                    Memuat daftar lembaga penerbit resmi...
                  </div>
                )}

                {/* Nomor WhatsApp Aktif (Wajib untuk Jalur Penerbit Resmi) */}
                <div className="p-3 rounded-xl bg-purple-50/70 border border-purple-200/90 space-y-1.5">
                  <label className="block text-xs font-bold text-purple-950">
                    Nomor WhatsApp Aktif Penulis <span className="text-rose-600">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-purple-600">
                      <Phone className="w-4 h-4" />
                    </div>
                    <input
                      type="tel"
                      value={formData.authorWhatsapp || ''}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, authorWhatsapp: e.target.value }))
                      }
                      placeholder="Contoh: 081234567890 atau 628123456789"
                      className={`w-full pl-9 pr-3.5 py-2 text-sm border rounded-xl bg-white transition duration-150 ${
                        formErrors.authorWhatsapp
                          ? 'border-rose-400 focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 bg-rose-50/20'
                          : 'border-purple-300 focus:outline-hidden focus:ring-2 focus:ring-purple-500/30 focus:border-purple-600'
                      }`}
                    />
                  </div>
                  <p className="text-[11px] text-purple-800 leading-tight">
                    Pihak penerbit akan menghubungi nomor WhatsApp ini untuk komunikasi, koordinasi editorial, dan verifikasi naskah.
                  </p>
                  {formErrors.authorWhatsapp && (
                    <p className="text-xs text-rose-600 font-semibold mt-1 flex items-center gap-1">
                      <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                      <span>{formErrors.authorWhatsapp}</span>
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div>
                <input
                  type="text"
                  readOnly
                  value="Penerbit Mandiri (Self-Publishing)"
                  className="w-full px-3.5 py-2 text-sm border border-slate-200 rounded-xl bg-slate-50 text-slate-600 font-medium"
                />
                <p className="text-[11px] text-slate-500 mt-1">
                  Naskah langsung diteruskan ke Admin Pusat UIBN tanpa melalui lembaga penerbit pihak ketiga.
                </p>
              </div>
            )}
            {formErrors.publisherName && (
              <p className="text-xs text-rose-500 mt-1 flex items-center gap-1 font-medium">
                <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                <span>{formErrors.publisherName}</span>
              </p>
            )}
          </div>
        ) : user?.role === 'penerbit' || user?.role === 'organisasi' ? (
          <div className="space-y-1.5">
            <div className="relative">
              <input
                type="text"
                readOnly
                value={user?.organization || user?.publisherLegalName || user?.name || ''}
                className="w-full pl-9 pr-24 py-2 text-sm border border-slate-200 rounded-xl bg-slate-100/80 text-slate-800 font-semibold cursor-not-allowed select-none focus:outline-hidden"
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                <Lock className="w-4 h-4" />
              </div>
              {onOpenProfile && (
                <button
                  type="button"
                  onClick={onOpenProfile}
                  className="absolute inset-y-1 right-1 px-2.5 bg-white hover:bg-slate-50 text-indigo-600 border border-slate-200 rounded-lg text-[10px] font-bold flex items-center gap-1 cursor-pointer transition shadow-2xs"
                  title={`Buka Pengaturan Profil untuk Melihat Info ${user?.role === 'organisasi' ? 'Organisasi / Komunitas' : 'Penerbit'}`}
                >
                  <span>Edit Profil</span>
                </button>
              )}
            </div>
            <p className="text-[11px] text-slate-500 leading-normal flex items-start gap-1">
              <Info className="w-3.5 h-3.5 text-indigo-500 shrink-0 mt-0.5" />
              <span>
                {user?.role === 'organisasi'
                  ? 'Nama organisasi/komunitas diambil otomatis dari akun terdaftar. Pendaftaran naskah diajukan langsung ke Admin UIBN.'
                  : 'Nama lembaga penerbit diambil otomatis dari profil akun terverifikasi. Pendaftaran naskah diajukan langsung ke Admin UIBN.'}
              </span>
            </p>
          </div>
        ) : (
          <>
            <input
              type="text"
              value={formData.publisherName}
              onChange={(e) => setFormData((prev) => ({ ...prev, publisherName: e.target.value }))}
              placeholder="Nama penerbit atau mandiri"
              className={`w-full px-3.5 py-2 text-sm border rounded-xl bg-white transition duration-150 ${
                formErrors.publisherName
                  ? 'border-rose-300 focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 bg-rose-50/20'
                  : 'border-slate-300 focus:outline-hidden focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-600'
              }`}
            />
            {formErrors.publisherName && (
              <p className="text-xs text-rose-500 mt-1 flex items-center gap-1 font-medium">
                <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                <span>{formErrors.publisherName}</span>
              </p>
            )}
          </>
        )}
      </div>

      {/* Publication Year */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="block text-xs font-semibold text-slate-700">
            Tahun Terbit <span className="text-rose-500">*</span>
          </label>
          {isApproved && (
            <span className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-800 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-md">
              <Lock className="w-3 h-3 text-amber-600" />
              <span>Terkunci (UIBN Terbit)</span>
            </span>
          )}
        </div>
        <div className="relative">
          <input
            type="number"
            min="1900"
            max="2100"
            readOnly={isApproved}
            value={formData.publicationYear}
            onChange={(e) =>
              !isApproved &&
              setFormData((prev) => ({
                ...prev,
                publicationYear: parseInt(e.target.value, 10) || 2026,
              }))
            }
            className={`w-full px-3.5 py-2 text-sm border rounded-xl transition duration-150 ${
              isApproved
                ? 'bg-slate-100/90 text-slate-800 border-slate-200 cursor-not-allowed select-none pl-9 font-semibold'
                : formErrors.publicationYear
                ? 'border-rose-300 focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 bg-rose-50/20 bg-white'
                : 'border-slate-300 focus:outline-hidden focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-600 bg-white'
            }`}
          />
          {isApproved && (
            <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          )}
        </div>
        {isApproved ? (
          <p className="text-[11px] text-slate-500 mt-1">
            Tahun terbit naskah telah tercatat permanen saat UIBN disetujui.
          </p>
        ) : (
          formErrors.publicationYear && (
            <p className="text-xs text-rose-500 mt-1 flex items-center gap-1 font-medium">
              <AlertCircle className="w-3.5 h-3.5 shrink-0" />
              <span>{formErrors.publicationYear}</span>
            </p>
          )
        )}
      </div>

      {/* Physical Specs & Edition Details Section */}
      <div className="sm:col-span-2 p-4 sm:p-5 lg:p-6 rounded-2xl bg-slate-50/75 border border-slate-200/80 space-y-4">
        <div className="flex items-center justify-between pb-2 border-b border-slate-200/60 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Layers className="w-4 h-4 text-indigo-600" />
            <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider">
              Spesifikasi Fisik, Bahasa & Silsilah Edisi
            </h4>
          </div>
          <span className="text-[11px] text-slate-500 font-medium">
            Ketebalan naskah, bahasa, edisi, dan catatan revisi
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
          {/* Pages */}
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Jumlah Halaman <span className="text-rose-500">*</span>
            </label>
            <input
              type="number"
              min="0"
              value={formData.pages}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  pages: parseInt(e.target.value, 10) || 0,
                }))
              }
              placeholder="Contoh: 240"
              className={`w-full px-3.5 py-2 text-sm border rounded-xl bg-white transition duration-150 ${
                formErrors.pages
                  ? 'border-rose-300 focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 bg-rose-50/20'
                  : 'border-slate-300 focus:outline-hidden focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-600'
              }`}
            />
            {formErrors.pages && (
              <p className="text-xs text-rose-500 mt-1 flex items-center gap-1 font-medium">
                <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                <span>{formErrors.pages}</span>
              </p>
            )}
          </div>

          {/* Edition / Print */}
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Edisi / Cetakan Ke-
            </label>
            <input
              type="text"
              value={formData.edition}
              onChange={(e) => setFormData((prev) => ({ ...prev, edition: e.target.value }))}
              placeholder="Contoh: 1, Cetakan 2, atau Edisi Revisi"
              className="w-full px-3.5 py-2 text-sm border border-slate-300 rounded-xl focus:outline-hidden focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-600 bg-white"
            />
          </div>

          {/* Language */}
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Bahasa Naskah / Buku
            </label>
            <input
              type="text"
              value={formData.language}
              onChange={(e) => setFormData((prev) => ({ ...prev, language: e.target.value }))}
              placeholder="Contoh: Indonesia, Inggris, Jawa"
              className="w-full px-3.5 py-2 text-sm border border-slate-300 rounded-xl focus:outline-hidden focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-600 bg-white"
            />
          </div>

          {/* Edition Type */}
          <div className="sm:col-span-1 lg:col-span-1">
            <CustomSelect
              label="Tipe Silsilah Edisi"
              value={formData.editionType}
              onChange={(val) =>
                setFormData((prev) => ({ ...prev, editionType: val as EditionType }))
              }
              options={editionTypeOptions}
              error={formErrors.editionType}
            />
          </div>

          {/* Edition Notes */}
          <div className="sm:col-span-1 lg:col-span-2">
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Catatan Perubahan Edisi (Opsional)
            </label>
            <input
              type="text"
              value={formData.editionNotes}
              onChange={(e) => setFormData((prev) => ({ ...prev, editionNotes: e.target.value }))}
              placeholder="Contoh: Penambahan bab 5, revisi glosarium, perbaikan typo"
              className="w-full px-3.5 py-2 text-sm border border-slate-300 rounded-xl focus:outline-hidden focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-600 bg-white"
            />
            <p className="text-[11px] text-slate-400 mt-1">
              Jelaskan ringkasan perubahan materi jika buku merupakan edisi turunan / revisi.
            </p>
          </div>
        </div>
      </div>

      {/* ISBN & Submission Status Section */}
      <div className="sm:col-span-2 p-4 sm:p-5 lg:p-6 rounded-2xl bg-slate-50/75 border border-slate-200/80 space-y-4">
        <div className="flex items-center justify-between pb-2 border-b border-slate-200/60 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Barcode className="w-4 h-4 text-indigo-600" />
            <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider">
              Identifikasi ISBN & Status Pengajuan
            </h4>
          </div>
          <span className="text-[11px] text-slate-500 font-medium">
            Kode ISBN opsional dan tahapan alur verifikasi UIBN
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:gap-5">
          {/* ISBN Original */}
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Nomor ISBN Buku (Opsional)
            </label>
            <input
              type="text"
              value={formData.isbnOriginal || ''}
              onChange={(e) => setFormData((prev) => ({ ...prev, isbnOriginal: e.target.value }))}
              placeholder="Contoh: 978-602-8512-11-2"
              className={`w-full px-3.5 py-2 text-sm border rounded-xl focus:outline-hidden focus:ring-2 bg-white ${
                formErrors.isbnOriginal
                  ? 'border-red-300 focus:ring-red-500/20 focus:border-red-500'
                  : 'border-slate-300 focus:ring-indigo-500/30 focus:border-indigo-600'
              }`}
            />
            {formErrors.isbnOriginal ? (
              <p className="mt-1 text-[11px] text-red-600 font-medium flex items-center gap-1">
                <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                <span>{formErrors.isbnOriginal}</span>
              </p>
            ) : (
              <p className="text-[11px] text-slate-400 mt-1">
                Format 10 atau 13 digit jika naskah sebelumnya sudah memiliki ISBN terdaftar.
              </p>
            )}
          </div>

          {/* Purchase URL */}
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1 flex items-center gap-1.5">
              <ShoppingCart className="w-3.5 h-3.5 text-indigo-600" />
              <span>Link Pembelian Buku (Opsional)</span>
            </label>
            <input
              type="url"
              value={formData.purchaseUrl || ''}
              onChange={(e) => setFormData((prev) => ({ ...prev, purchaseUrl: e.target.value }))}
              placeholder="Contoh: https://tokopedia.link/... atau https://shopee.co.id/..."
              className={`w-full px-3.5 py-2 text-sm border rounded-xl focus:outline-hidden focus:ring-2 bg-white ${
                formErrors.purchaseUrl
                  ? 'border-red-300 focus:ring-red-500/20 focus:border-red-500'
                  : 'border-slate-300 focus:ring-indigo-500/30 focus:border-indigo-600'
              }`}
            />
            <p className="text-[11px] text-slate-400 mt-1">
              Tautkan ke toko online (Tokopedia, Shopee, Gramedia) agar pembaca bisa langsung membeli karya Anda.
            </p>
          </div>

          {/* Status Selector */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-xs font-semibold text-slate-700">Status Pengajuan</label>
              {isApproved && (
                <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-800 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-md">
                  <Lock className="w-3 h-3 text-emerald-600" />
                  <span>Terkunci (UIBN Terbit)</span>
                </span>
              )}
            </div>
            <CustomSelect
              value={formData.status}
              onChange={(val) => !isApproved && setFormData((prev) => ({ ...prev, status: val as any }))}
              options={statusOptions}
              disabled={isApproved}
              error={formErrors.status}
            />
            {isApproved ? (
              <p className="text-[11px] text-slate-500 mt-1">
                Status pengajuan telah disetujui resmi oleh UIBN dan tidak dapat diubah kembali.
              </p>
            ) : (
              <p className="text-[11px] text-slate-400 mt-1">
                Pilih status draf awal atau langsung kirimkan pengajuan ke antrean verifikasi.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Cover URL & Auto Image Compression */}
      <div className="sm:col-span-2 p-4 rounded-2xl bg-slate-50/80 border border-slate-200/80 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <label className="block text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center gap-1.5">
            <Image className="w-4 h-4 text-indigo-600" />
            <span>Sampul Buku & Kompresi Otomatis</span>
          </label>

          <span className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-md flex items-center gap-1">
            <Zap className="w-3 h-3 text-emerald-600" />
            <span>Lazy Loading & WebP Auto-Compress Enabled</span>
          </span>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 items-start">
          {/* Live Lazy Book Cover Preview */}
          <div className="shrink-0 space-y-1">
            <span className="text-[10px] text-slate-400 font-semibold block text-center">Pratinjau Sampul</span>
            <LazyBookCover
              coverUrl={formData.coverUrl}
              title={formData.title || 'Pratinjau'}
              className="w-20 h-28 rounded-xl shadow-xs"
              targetWidth={200}
              quality={75}
            />
          </div>

          <div className="flex-1 min-w-0 space-y-3 w-full">
            {/* Direct URL Input */}
            <div>
              {(() => {
                const isUploadedCover = Boolean(
                  formData.coverUrl &&
                    (formData.coverUrl.includes('/api/images/') ||
                      formData.coverUrl.includes('r2.dev') ||
                      formData.coverUrl.includes('r2.cloudflarestorage.com') ||
                      formData.coverUrl.includes('cloudflare') ||
                      formData.coverUrl.startsWith('data:image/') ||
                      formData.coverUrl.startsWith('blob:'))
                );

                return (
                  <>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="block text-[11px] font-semibold text-slate-700">
                        Tautkan URL Gambar Sampul
                      </label>
                      {formData.coverUrl && (
                        <div className="flex items-center gap-2">
                          {isConfirmingDelete ? (
                            <div className="flex items-center gap-1.5 bg-rose-50 border border-rose-200 text-rose-800 text-[10px] px-2 py-1 rounded-xl animate-in fade-in zoom-in-95 duration-150 shadow-xs">
                              <span className="font-medium">Hapus sampul dari R2?</span>
                              <button
                                type="button"
                                disabled={isDeletingCover}
                                onClick={async () => {
                                  const urlToDelete = formData.coverUrl;
                                  setIsDeletingCover(true);
                                  setFormData((prev) => ({ ...prev, coverUrl: '' }));
                                  setCompressionStats(null);
                                  setIsConfirmingDelete(false);
                                  if (urlToDelete) {
                                    await deleteRemoteCover(urlToDelete);
                                  }
                                  setIsDeletingCover(false);
                                }}
                                className="px-2 py-0.5 bg-rose-600 hover:bg-rose-700 text-white font-bold rounded-lg transition cursor-pointer disabled:opacity-50"
                              >
                                {isDeletingCover ? 'Menghapus...' : 'Ya, Hapus'}
                              </button>
                              <button
                                type="button"
                                disabled={isDeletingCover}
                                onClick={() => setIsConfirmingDelete(false)}
                                className="px-2 py-0.5 bg-slate-200 hover:bg-slate-300 text-slate-700 font-semibold rounded-lg transition cursor-pointer"
                              >
                                Batal
                              </button>
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={() => setIsConfirmingDelete(true)}
                              className="text-[10px] font-semibold text-rose-600 hover:text-rose-700 hover:underline cursor-pointer"
                              title="Hapus sampul & bersihkan berkas di Cloudflare R2"
                            >
                              Reset / Hapus
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="relative">
                      <input
                        type="url"
                        value={formData.coverUrl}
                        readOnly={isUploadedCover}
                        onChange={(e) => {
                          if (!isUploadedCover) {
                            setFormData((prev) => ({ ...prev, coverUrl: e.target.value }));
                            setCompressionStats(null);
                          }
                        }}
                        placeholder="https://..."
                        className={`w-full px-3.5 py-2 text-sm border rounded-xl transition duration-150 ${
                          isUploadedCover
                            ? 'bg-slate-100 text-slate-600 border-slate-300/90 cursor-not-allowed select-all'
                            : 'bg-white border-slate-300 focus:outline-hidden focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-600 text-slate-900'
                        }`}
                      />
                      {isUploadedCover && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                          <Lock className="w-3.5 h-3.5 text-slate-400" />
                        </div>
                      )}
                    </div>
                    {isUploadedCover ? (
                      <p className="text-[10px] text-slate-500 mt-1 flex items-center gap-1">
                        <Lock className="w-3 h-3 text-slate-400 shrink-0" />
                        <span>Tautan gambar terisi dari berkas unggahan R2 (Read-Only). Klik <strong>Reset / Hapus</strong> untuk mengganti.</span>
                      </p>
                    ) : (
                      <p className="text-[10px] text-slate-400 mt-1">
                        Masukkan tautan langsung gambar sampul dari web atau unggah berkas di bawah.
                      </p>
                    )}
                  </>
                );
              })()}
            </div>

            {/* Local File Upload with Auto WebP Compression */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-[11px] font-semibold text-slate-600">
                  Atau Unggah File Gambar (Otomatis Kompresi WebP & Resize)
                </label>
                <span className="text-[10px] font-medium text-slate-400">
                  Maks. 5 MB
                </span>
              </div>
              <div className="relative">
                <input
                  type="file"
                  accept="image/webp,image/jpeg,image/png"
                  disabled={isCompressingImage}
                  onChange={handleCoverFileUpload}
                  className={`block w-full text-xs text-slate-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-xl file:border-0 file:text-xs file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100 cursor-pointer border rounded-xl bg-white p-1 transition duration-150 ${
                    uploadError ? 'border-rose-300 ring-1 ring-rose-500/20' : 'border-slate-200'
                  }`}
                />
              </div>

              {uploadError && (
                <div className="mt-2 text-[11px] text-rose-600 font-medium flex items-center gap-1.5 animate-in fade-in slide-in-from-top-1 duration-200">
                  <AlertCircle className="w-3.5 h-3.5" />
                  <span>{uploadError}</span>
                </div>
              )}

              {/* WebP Conversion In-Progress Animation Banner */}
              {isCompressingImage && (
                <div className="mt-3 p-3.5 rounded-2xl bg-indigo-50/90 border border-indigo-200/80 text-indigo-900 animate-in fade-in zoom-in-95 duration-200 shadow-xs space-y-2.5">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className="relative flex items-center justify-center w-7 h-7 rounded-xl bg-indigo-600 text-white shadow-md shadow-indigo-600/30 shrink-0">
                        <Sparkles className="w-4 h-4 animate-spin" style={{ animationDuration: '2.5s' }} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs font-bold text-indigo-950 truncate">
                          Sedang Mengonversi ke WebP...
                        </p>
                        <p className="text-[10px] text-indigo-600 font-medium">
                          Mengoptimalkan resolusi & kompresi cerdas lossless
                        </p>
                      </div>
                    </div>
                    <span className="shrink-0 px-2 py-0.5 rounded-full bg-indigo-200/70 text-indigo-800 text-[10px] font-extrabold tracking-wider uppercase animate-pulse">
                      Memproses
                    </span>
                  </div>

                  {/* Animated gradient progress bar */}
                  <div className="w-full bg-indigo-200/60 h-1.5 rounded-full overflow-hidden relative">
                    <div 
                      className="absolute inset-0 bg-linear-to-r from-indigo-500 via-purple-500 to-indigo-600 rounded-full animate-pulse"
                      style={{
                        backgroundSize: '200% 100%',
                      }}
                    />
                  </div>
                </div>
              )}

              {/* WebP Conversion Result: Before & After File Size Display */}
              {compressionStats && !isCompressingImage && (
                <div className="relative mt-3 p-3.5 sm:p-4 rounded-2xl bg-emerald-50/90 border border-emerald-200/90 text-emerald-900 animate-in fade-in slide-in-from-top-1 duration-200 shadow-xs space-y-2.5">
                  {/* Close button at top-right corner */}
                  <button
                    type="button"
                    onClick={() => setCompressionStats(null)}
                    aria-label="Tutup info hasil konversi"
                    className="absolute top-2.5 right-2.5 sm:top-3 sm:right-3 p-1 rounded-lg text-emerald-700 hover:text-emerald-950 hover:bg-emerald-200/60 transition cursor-pointer"
                    title="Tutup kartu rincian"
                  >
                    <X className="w-4 h-4" />
                  </button>

                  <div className="flex items-center justify-between flex-wrap gap-2 pr-7">
                    <div className="flex items-center gap-2">
                      <div className="flex items-center justify-center w-6 h-6 rounded-lg bg-emerald-600 text-white shadow-xs shrink-0">
                        <CheckCircle2 className="w-3.5 h-3.5" />
                      </div>
                      <span className="font-bold text-emerald-950 text-xs">
                        Konversi WebP Berhasil Dioptimalkan!
                      </span>
                    </div>
                    {compressionStats.savingsPercentage > 0 && (
                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-emerald-600 text-white font-black text-[10px] shadow-xs">
                        <TrendingDown className="w-3 h-3" />
                        <span>Hemat {compressionStats.savingsPercentage}% Ukuran</span>
                      </span>
                    )}
                  </div>

                  {/* Size Comparison Grid */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-[11px]">
                    <div className="p-2.5 rounded-xl bg-white/90 border border-emerald-100 flex flex-col justify-between">
                      <span className="text-[10px] text-slate-500 font-medium flex items-center gap-1">
                        <FileImage className="w-3 h-3 text-slate-400" />
                        <span>Ukuran Sebelum:</span>
                      </span>
                      <span className="font-bold text-slate-700 text-xs mt-1">
                        {compressionStats.originalSizeKB} KB
                      </span>
                    </div>

                    <div className="p-2.5 rounded-xl bg-emerald-100/60 border border-emerald-200 flex flex-col justify-between">
                      <span className="text-[10px] text-emerald-700 font-medium flex items-center gap-1">
                        <Zap className="w-3 h-3 text-emerald-600" />
                        <span>Ukuran Setelah:</span>
                      </span>
                      <span className="font-extrabold text-emerald-900 text-xs mt-1">
                        {compressionStats.compressedSizeKB} KB (WebP)
                      </span>
                    </div>

                    <div className="col-span-2 sm:col-span-1 p-2.5 rounded-xl bg-white/90 border border-emerald-100 flex flex-col justify-between">
                      <span className="text-[10px] text-slate-500 font-medium">Format Output:</span>
                      <span className="font-bold text-indigo-600 text-xs mt-1">
                        image/webp ⚡
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Purchase / Marketplace URL */}
      <div>
        <label className="block text-xs font-semibold text-slate-700 mb-1">
          Link Pembelian Buku (Opsional)
        </label>
        <input
          type="url"
          value={formData.purchaseUrl}
          onChange={(e) => setFormData((prev) => ({ ...prev, purchaseUrl: e.target.value }))}
          placeholder="https://tokopedia.com/... atau web penerbit"
          className={`w-full px-3.5 py-2 text-sm border rounded-xl bg-white transition duration-150 ${
            formErrors.purchaseUrl
              ? 'border-rose-300 focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 bg-rose-50/20'
              : 'border-slate-300 focus:outline-hidden focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-600'
          }`}
        />
        {formErrors.purchaseUrl && (
          <p className="text-xs text-rose-500 mt-1 flex items-center gap-1 font-medium">
            <AlertCircle className="w-3.5 h-3.5 shrink-0" />
            <span>{formErrors.purchaseUrl}</span>
          </p>
        )}
      </div>

      {/* Synopsis & Summary */}
      <div className="sm:col-span-2">
        <div className="flex items-center justify-between mb-1.5">
          <label className="block text-xs font-semibold text-slate-700">
            Sinopsis & Ikhtisar Naskah Buku
          </label>
          <span className="text-[11px] text-slate-400">
            Format editorial kaya (Bold, Italic, List, Quote)
          </span>
        </div>
        {isFormContentsLoading ? (
          <div className="w-full h-40 bg-slate-50 border border-slate-200/60 rounded-xl p-4.5 space-y-3.5 relative overflow-hidden animate-pulse">
            <div className="h-4 bg-slate-200/80 rounded-md w-1/4" />
            <div className="h-3.5 bg-slate-200/50 rounded-md w-full" />
            <div className="h-3.5 bg-slate-200/50 rounded-md w-11/12" />
            <div className="h-3.5 bg-slate-200/50 rounded-md w-4/5" />
          </div>
        ) : (
          <RichTextEditor
            content={formData.synopsis || ''}
            onChange={(html) => setFormData((prev) => ({ ...prev, synopsis: html }))}
            placeholder="Tuliskan ringkasan alur cerita, pokok bahasan ilmiah, atau intisari naskah untuk ditampilkan pada hasil verifikasi publik dan sertifikat resmi UIBN..."
          />
        )}
        <p className="text-[11px] text-slate-400 mt-1.5">
          Tip: Gunakan format <b>Tebal</b>, <i>Miring</i>, <u>Garis Bawah</u>, daftar butir/angka,
          atau blok kutipan agar sinopsis tampil memikat saat diverifikasi pembaca.
        </p>
      </div>

      {/* Table of Contents (Daftar Isi Buku) */}
      <div className="sm:col-span-2">
        <div className="flex items-center justify-between mb-1.5 flex-wrap gap-1.5">
          <label className="block text-xs font-semibold text-slate-700 flex items-center gap-1.5">
            <ListOrdered className="w-4 h-4 text-indigo-600" />
            <span>Daftar Isi Buku (Table of Contents)</span>
          </label>
          {!isFormContentsLoading && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  const sampleToc = `Bab 1: Pendahuluan dan Latar Belakang (hal. 1)
Bab 2: Landasan Teori & Konsep Utama (hal. 25)
Bab 3: Metodologi & Pembahasan Naskah (hal. 60)
Bab 4: Analisis dan Hasil Kajian (hal. 120)
Bab 5: Kesimpulan dan Rekomendasi (hal. 180)
Daftar Pustaka & Indeks (hal. 195)`;
                  const currentClean = cleanTableOfContents(formData.tableOfContents);
                  setFormData((prev) => ({
                    ...prev,
                    tableOfContents: currentClean ? `${currentClean}\n${sampleToc}` : sampleToc,
                  }));
                }}
                className="inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-600 hover:text-indigo-800 bg-indigo-50 hover:bg-indigo-100 px-2 py-0.5 rounded-md transition cursor-pointer"
              >
                <Sparkles className="w-3 h-3" />
                <span>+ Sisipkan Format Contoh</span>
              </button>
              {hasValidTableOfContents(formData.tableOfContents) && (
                <button
                  type="button"
                  onClick={() => setFormData((prev) => ({ ...prev, tableOfContents: '' }))}
                  className="text-[11px] text-slate-400 hover:text-rose-600 transition cursor-pointer"
                >
                  Hapus
                </button>
              )}
            </div>
          )}
        </div>

        {isFormContentsLoading ? (
          <div className="w-full h-48 bg-slate-50 border border-slate-200/60 rounded-xl p-4.5 space-y-4 relative overflow-hidden animate-pulse">
            <div className="flex justify-between items-center">
              <div className="h-4 bg-slate-200/80 rounded-md w-1/3" />
              <div className="h-4 bg-slate-200/80 rounded-md w-12" />
            </div>
            <div className="flex justify-between items-center">
              <div className="h-3.5 bg-slate-200/50 rounded-md w-1/2" />
              <div className="h-3.5 bg-slate-200/50 rounded-md w-10" />
            </div>
            <div className="flex justify-between items-center">
              <div className="h-3.5 bg-slate-200/50 rounded-md w-5/12" />
              <div className="h-3.5 bg-slate-200/50 rounded-md w-8" />
            </div>
            <div className="flex justify-between items-center">
              <div className="h-3.5 bg-slate-200/50 rounded-md w-7/12" />
              <div className="h-3.5 bg-slate-200/50 rounded-md w-11" />
            </div>
          </div>
        ) : (
          <textarea
            rows={6}
            value={cleanTableOfContents(formData.tableOfContents)}
            onChange={(e) => setFormData((prev) => ({ ...prev, tableOfContents: e.target.value }))}
            placeholder={`Bab 1: Pendahuluan (hal. 1)\nBab 2: Pembahasan Utama (hal. 25)\nBab 3: Hasil dan Analisis (hal. 70)\nBab 4: Kesimpulan (hal. 130)\nDaftar Pustaka (hal. 145)`}
            className="w-full px-3.5 py-2.5 text-xs sm:text-sm font-mono border border-slate-300 rounded-xl focus:outline-hidden focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-600 bg-white leading-relaxed text-slate-800 placeholder:text-slate-400 placeholder:font-sans"
          />
        )}

        <div className="mt-2 p-2.5 rounded-xl bg-slate-50 border border-slate-200/80 text-[11px] text-slate-600 flex items-start gap-2">
          <Info className="w-3.5 h-3.5 text-slate-500 shrink-0 mt-0.5" />
          <p className="leading-relaxed">
            Daftar isi naskah membantu kurator penerbit dan verifikator UIBN memahami struktur buku secara menyeluruh. Tuliskan bab per baris beserta perkiraan nomor halaman jika ada.
          </p>
        </div>
      </div>
    </div>
  );
};

